/**
 * The full reviews list — the storefront's answer to the reference's reviews
 * view (`shop.app/m/johnelliott`, measured at 414 and 1440).
 *
 * The reference's controls, in its order:
 *   1. average + star + rating count
 *   2. a SEARCH field, full width, 44px tall, placeholder "Search"
 *   3. a chip row: "Sort by" and "Rating", 40px tall, glass
 *   4. Sort — five options, single select: Most relevant / Newest / Oldest /
 *      Rating: Low → High / Rating: High → Low, with Reset and Done
 *   5. Rating — 5…1, MULTI select (a checkbox per row, not one radio), with
 *      Reset and Done
 *   6. the review items themselves (see `ReviewCard`)
 *
 * What the API can and cannot do, and why the split is where it is:
 *
 *   sort=recommended / sort=latest   real server sorts — asked for.
 *   Oldest, Rating asc/desc          NOT server sorts. Sorted here, over the
 *                                    page the server returned.
 *   search, rating filter            no server parameters exist. Applied here.
 *
 * That is honest for the sizes this endpoint serves (it caps at 200 rows and
 * we ask for the cap), and it is written down in the report as a server gap
 * rather than hidden — a product with more than 200 reviews would search and
 * filter only the first 200.
 *
 * ── WHERE THE CONTROLS LIVE NOW ───────────────────────────────────────────
 *
 * The search field, the two chips, their menus and the histogram were lifted
 * out of this file into `ReviewsFilterBar` / `ReviewsHistogram`, and the
 * sort-query-stars state into `useReviewsControls`, when the SHOP's reviews
 * needed the same three controls (2026-10-14). Nothing about this sheet
 * changed in doing so — same elements, same classes, same `data-testid`s —
 * and that is the point: one definition of "Rating: High → Low", one
 * diacritic fold, two lists.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import AnimatedModal from '../../Components/AnimatedModal';
import { listProductReviews, getProductReviewSummary, toggleReviewHelpful } from '../../api/storeApi';
import { reviewerDisplayName, reviewPhotos, variantLabel } from '../../utils/reviewsDisplay';
import ReviewLightbox from './ReviewLightbox';
import ReviewCard, { StarsRow } from './ReviewCard';
import ReviewsFilterBar, { ReviewsHistogram } from './ReviewsFilterBar';
import useReviewsControls from './useReviewsControls';
import { normalizeForSearch, textMatches } from './reviewsFiltering';
import styles from './reviewsModal.module.css';

/**
 * Sort keys. `apiSort` is what the server understands; `compare` is the
 * client-side ordering applied on top (the server has no oldest/by-rating
 * sort, so those two are ours). Keeping both on one row is what stops the
 * two halves from drifting.
 */
const SORTS = [
  { value: 'relevant', apiSort: 'recommended', key: 'reviews.sortRelevant', label: 'Most relevant' },
  { value: 'latest', apiSort: 'latest', key: 'reviews.sortNewest', label: 'Newest' },
  { value: 'oldest', apiSort: 'latest', key: 'reviews.sortOldest', label: 'Oldest', compare: (a, b) => ts(a) - ts(b) },
  { value: 'stars_asc', apiSort: 'latest', key: 'reviews.sortStarsAsc', label: 'Rating: Low → High', compare: (a, b) => (a.stars - b.stars) || (ts(b) - ts(a)) },
  { value: 'stars_desc', apiSort: 'latest', key: 'reviews.sortStarsDesc', label: 'Rating: High → Low', compare: (a, b) => (b.stars - a.stars) || (ts(b) - ts(a)) },
];

const ts = (r) => new Date(r?.createdAt || 0).getTime() || 0;
const DEFAULT_SORT = 'relevant';

/**
 * Case- and diacritic-insensitive contains. The implementation moved to
 * `reviewsFiltering` so the shop's reviews fold a search term the same way;
 * it is re-exported here because this is the path the suite imports.
 */
export { normalizeForSearch };

/** Does this review match the free-text query? Body, reviewer, variant. */
export function reviewMatches(review, query) {
  /* THE HAYSTACK IS WHAT THE SHOPPER CAN SEE, and that is a privacy rule
   * rather than a tidiness one. It used to include `customer.fullName`
   * alongside the displayed name, which turns the filter into an oracle: type
   * a surname, watch the review survive, and the surname the page deliberately
   * withheld is confirmed. Now that the visible name is a real first name that
   * matters more, not less. */
  return textMatches(searchParts(review), query);
}

const searchParts = (review) => [
  review?.body,
  reviewerDisplayName(review?.customer?.fullName),
  variantLabel(review),
  review?.reply?.body,
];

const ReviewsModal = ({ open = true, productId, product, productHref, onClose, t, shopName, shopLogo }) => {
  const { i18n } = useTranslation();
  const locale = i18n?.language;
  const [lightbox, setLightbox] = useState(null);
  const [summary, setSummary] = useState({ count: 0, average: 0, histogram: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } });
  const [reviews, setReviews] = useState([]);
  const [photosOnly, setPhotosOnly] = useState(false);
  const [variantFilter, setVariantFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busyReviewId, setBusyReviewId] = useState(null);

  /* THIS SHEET'S OWN FILTERS, handed to the shared hook as one predicate.
   * They are not in the hook because the shop's list has neither photos nor
   * variants — and a hook that knew about both would be the copy this split
   * was made to avoid. */
  const extraFilter = useCallback((r) => {
    if (photosOnly && reviewPhotos(r).length === 0) return false;
    if (variantFilter !== 'all' && variantLabel(r) !== variantFilter) return false;
    return true;
  }, [photosOnly, variantFilter]);

  const clearExtra = useCallback(() => {
    setPhotosOnly(false);
    setVariantFilter('all');
  }, []);

  const getStars = useCallback((r) => r.stars, []);
  const getSearchParts = useCallback((r) => searchParts(r), []);

  const {
    sort, setSort, activeSort,
    query, setQuery,
    stars, toggleStar, resetStars,
    clearAll, filtersActive,
    visible: visibleReviews,
  } = useReviewsControls({
    rows: reviews,
    sortOptions: SORTS,
    defaultSort: DEFAULT_SORT,
    getStars,
    getSearchParts,
    extraFilter,
    extraActive: photosOnly || variantFilter !== 'all',
    onClearExtra: clearExtra,
  });

  const apiSort = activeSort.apiSort;

  useEffect(() => {
    if (!open || !productId) return undefined;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getProductReviewSummary(productId),
      listProductReviews(productId, { sort: apiSort, limit: 200 }),
    ]).then(([s, list]) => {
      if (cancelled) return;
      if (s) setSummary(s);
      setReviews(Array.isArray(list) ? list : []);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [productId, apiSort, open]);

  const variantOptions = useMemo(() => {
    const seen = new Set();
    for (const r of reviews) { const l = variantLabel(r); if (l) seen.add(l); }
    return [...seen];
  }, [reviews]);

  const handleHelpful = async (id) => {
    if (busyReviewId) return;
    setBusyReviewId(id);
    try {
      const r = await toggleReviewHelpful(id);
      setReviews((prev) => prev.map((rv) => (rv.id === id ? { ...rv, helpfulCount: r.count, helpfulByMe: r.helpful } : rv)));
    } catch (_) { /* a guest vote 401s; the row simply stays as it was */ }
    finally { setBusyReviewId(null); }
  };

  return (
    <AnimatedModal
      open={open}
      onClose={onClose}
      sheetClassName={styles.sheet}
      testId="reviews-modal"
      ariaLabel={t('reviews.title', { defaultValue: 'Reviews' })}
    >
      {({ requestClose }) => (
        <>
          <button
            type="button" className={styles.closeBtn}
            onClick={requestClose}
            aria-label={t('common.close', { defaultValue: 'Close' })}
            data-testid="reviews-modal-close"
          >
            <FiX />
          </button>

          <div className={styles.header}>
            <h2 className={styles.title}>{t('reviews.title', { defaultValue: 'Reviews' })}</h2>
            <div className={styles.avgRow}>
              <span className={styles.avgValue}>{Number(summary.average || 0).toFixed(1)}</span>
              <StarsRow value={summary.average} size={22} t={t} testId="reviews-modal-average-stars" />
              <span className={styles.avgCount}>
                {t('reviews.ratingsCount', { defaultValue: '{{n}} ratings', n: summary.count, count: summary.count })}
              </span>
            </div>
          </div>

          <ReviewsFilterBar
            t={t}
            testIdPrefix="reviews-modal"
            query={query}
            onQuery={setQuery}
            searchAria={t('reviews.searchAria', {
              defaultValue: 'Search reviews for {{name}}',
              name: product?.name || shopName || '',
            })}
            sort={sort}
            onSort={setSort}
            sortOptions={SORTS}
            defaultSort={DEFAULT_SORT}
            stars={stars}
            onToggleStar={toggleStar}
            onResetStars={resetStars}
            histogram={summary.histogram}
            filtersActive={filtersActive}
            onClearAll={clearAll}
          >
            {/* Kept from the previous build — they are real filters this shop
                has and the reference simply does not sell variants the same
                way. They ride the same chip recipe. */}
            <button
              type="button"
              className={`${styles.chip} ${photosOnly ? styles.chipOn : ''}`}
              onClick={() => setPhotosOnly((v) => !v)}
              aria-pressed={photosOnly}
              data-testid="reviews-modal-filter-photos"
            >
              {t('reviews.filterWithPhotos', { defaultValue: 'With photos' })}
            </button>
            {variantOptions.map((label) => (
              <button
                key={label}
                type="button"
                className={`${styles.chip} ${variantFilter === label ? styles.chipOn : ''}`}
                onClick={() => setVariantFilter((v) => (v === label ? 'all' : label))}
                aria-pressed={variantFilter === label}
                data-testid={`reviews-modal-filter-variant-${label}`}
              >
                {label}
              </button>
            ))}
          </ReviewsFilterBar>

          <ReviewsHistogram
            histogram={summary.histogram}
            stars={stars}
            onToggleStar={toggleStar}
            t={t}
            testIdPrefix="reviews-modal"
          />

          {loading ? (
            <div className={styles.emptyMsg}>{t('common.loading', { defaultValue: 'Loading…' })}</div>
          ) : visibleReviews.length === 0 ? (
            <div className={styles.emptyMsg} data-testid={reviews.length ? 'reviews-modal-no-results' : 'reviews-modal-empty'}>
              {reviews.length
                ? t('reviews.noMatches', { defaultValue: 'No reviews match these filters.' })
                : t('reviews.empty', { defaultValue: 'No reviews yet. Be the first!' })}
            </div>
          ) : (
            <div className={styles.list}>
              {visibleReviews.map((r) => (
                <ReviewCard
                  key={r.id}
                  review={r}
                  product={product}
                  productHref={productHref}
                  onHelpful={handleHelpful}
                  helpfulBusy={busyReviewId === r.id}
                  onPhoto={(photos, index) => setLightbox({ photos, index })}
                  t={t}
                  locale={locale}
                  shopName={shopName}
                  shopLogo={shopLogo}
                />
              ))}
            </div>
          )}

          {lightbox && (
            <ReviewLightbox
              photos={lightbox.photos}
              index={lightbox.index}
              onClose={() => setLightbox(null)}
              onIndex={(i) => setLightbox((lb) => lb && ({ ...lb, index: i }))}
            />
          )}
        </>
      )}
    </AnimatedModal>
  );
};

export default ReviewsModal;
